// ===================================================================================================
// InstantQuotesSearchConfig.jsx
// ---------------------------------------------------------------------------------------------------
// Search table for Instant Quotes (Rate Queries) with Create/View actions.
// - Keeps action handlers stable to prevent unnecessary rerenders in MTable.
// - Stores operation and selected row into the global state before opening the CRUD tab.
// - Uses i18n keys for all user-facing labels and messages.
// - Hides Edit/Copy/Print from the context menu to match the intended UX.
// ---------------------------------------------------------------------------------------------------

import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function InstantQuotesSearchConfig({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);                     // theme/colors and loading helpers
  const dispatch = useDispatch();                             // redux dispatcher
  const { t } = useTranslation();                             // i18n hook
  const [refresh, setRefresh] = useState(false);              // toggle that MTable listens to

  // Context items to remove from the MTable row menu for this page
  const removeContextItems = useMemo(() => ['Update', 'Print', 'Copy'], []);

  // Single source of truth for the CRUD route path used when opening the detail tab
  const CRUD_ROUTE = 'instant-quotes/instant-quotesConfigCrud';

  //====================================================
  // openRateQueryCrud
  // Opens or focuses the CRUD tab for Rate Queries.
  // Persists the operation and, when required, the selected row into global state.
  // Shows a localized error toast if View is requested without a selection.
  //====================================================
  const openRateQueryCrud = useCallback(
    (operation, row) => {
      // Persist the requested operation so the CRUD screen knows what to render
      dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

      // For view-like operations we need a selected row
      if (['View'].includes(operation)) {
        if (!row) {
          toastMessages('error', '', t('SelectedLine')); // "Select a line" message
          return;
        }
        // Keep compatibility with downstream consumers that expect this key
        dispatch(M_StateActions.addM_State({ key: 'InstantQuoteSearchLine', value: row }));
      }

      // Build the tab label using i18n to keep text outside code
      const tabLabel =
        operation === 'Create'
          ? t('LabelNewInstantQuote')
          : t('LabelViewInstantQuote', { name: row?.Name ?? '' });

      // Open or focus the CRUD tab for the requested operation
      onCreateTab(CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab]
  );

  //====================================================
  // replaceFunctions
  // Map MTable actions to our stable openRateQueryCrud callback.
  // Using useMemo ensures the map identity is stable across renders.
  //====================================================
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ['View', (row) => openRateQueryCrud('View', row)], // row required
        ['Create', () => openRateQueryCrud('Create')],     // no row needed
      ]),
    [openRateQueryCrud]
  );

  return (
    <div className="divGlobalSettings">
      <div className="subTitleSettings">
        <div>
          {/* Page title and supporting text come from i18n */}
          <div style={{ color: ctx.secondaryColor }}>{t('InstantQuotesSearch')}</div>
          <div className="labelSubtitleSettings">{t('InfoInstantQuotesSearch')}</div>
        </div>
      </div>

      <MTable
        // Core data source for the table (Rate Queries domain)
        entityName="CargofiveRateQueries"
        roleName="InstantQuotes"
        rolePermissions={roles?.map((vl) => vl.Value)}   // pass user role values for permission gating

        // Table UX
        filtersOnTable
        removeContextItems={removeContextItems}          // hide actions we do not support here
        useSessionStorage={false}                        // let the page manage its own state

        // Refresh wiring: parent toggles this and MTable refetches
        refresh={refresh}
        setRefresh={setRefresh}

        // Action overrides: Create/View go through our stable callback
        replaceFunctions={replaceFunctions}
        tabsRailWidth={250}
      />
    </div>
  );
}

InstantQuotesSearchConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};
